// Unified storage for "Recent searches" + "Recently viewed hotels/flights".
//
// Persists to localStorage and broadcasts a `recent-updated` CustomEvent so
// every rail on the page stays in sync without prop drilling.
//
// Hotels-specific behaviour (production):
//   • Only recorded when the user actually opens a hotel via View Rooms
//     (fired inside HotelDetailPage's mount effect).
//   • Cap 10 rows, dedupe by hotel_id, most-recent first.
//   • Expire entries older than 30 days on every read.
//   • Sync with the customer account when the user is logged in — batch on
//     login, single upsert on each view. Guests keep local-only state.

import { useEffect, useState } from 'react';
import { api } from './api';

const KEYS = {
  searches: 'elite_recent_searches_unified',
  hotels:   'elite_recent_viewed_hotels',
  flights:  'elite_recent_viewed_flights',
};
const MAX_SEARCHES = 3;
const MAX_VIEWED_FLIGHTS = 5;
const MAX_VIEWED_HOTELS = 10;
const HOTEL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const read = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch { return []; }
};

const write = (k, val) => {
  try {
    localStorage.setItem(k, JSON.stringify(val));
    window.dispatchEvent(new CustomEvent('recent-updated', { detail: { key: k } }));
  } catch { /* quota / privacy mode */ }
};

const dedupeAndLimit = (list, entry, max, keyFn) => {
  const key = keyFn(entry);
  return [entry, ...list.filter((it) => keyFn(it) !== key)].slice(0, max);
};

const isCustomerLoggedIn = () => {
  try { return !!localStorage.getItem('elite_customer_token'); } catch { return false; }
};

// ─── Recent searches ─────────────────────────────────────────────────────
export const recordSearch = (entry) => {
  const ts = Date.now();
  const list = read(KEYS.searches);
  const next = dedupeAndLimit(list, { ...entry, ts }, MAX_SEARCHES, (e) => `${e.module}|${e.url}`);
  write(KEYS.searches, next);
};
export const getRecentSearches = () => read(KEYS.searches);
export const clearRecentSearches = () => write(KEYS.searches, []);

// ─── Recently viewed hotels ──────────────────────────────────────────────
// Snapshot shape (all fields optional except id/name):
// { id, name, image, city, area, provider, star_rating, review_rating,
//   google_rating, price, currency, check_in, check_out, rooms, guests, ts }
export const recordHotelView = (hotel) => {
  if (!hotel?.id) return;
  const entry = {
    id: String(hotel.id),
    name: hotel.name,
    image: hotel.image,
    city: hotel.city,
    area: hotel.area,
    provider: hotel.provider,
    star_rating: hotel.star_rating ?? hotel.category ?? null,
    review_rating: hotel.review_rating ?? hotel.rating ?? null,
    google_rating: hotel.google_rating ?? null,
    price: hotel.price ?? null,
    currency: hotel.currency || 'INR',
    check_in: hotel.check_in || null,
    check_out: hotel.check_out || null,
    rooms: hotel.rooms || null,
    guests: hotel.guests || null,
    ts: Date.now(),
  };
  const list = read(KEYS.hotels);
  const next = dedupeAndLimit(list, entry, MAX_VIEWED_HOTELS, (e) => e.id);
  write(KEYS.hotels, next);

  // Fire-and-forget sync for logged-in users.
  if (isCustomerLoggedIn()) {
    api.post('/customer/recent-hotels', {
      hotel_id: entry.id,
      name: entry.name,
      image: entry.image,
      city: entry.city,
      area: entry.area,
      provider: entry.provider,
      star_rating: entry.star_rating,
      review_rating: entry.review_rating,
      google_rating: entry.google_rating,
      price: entry.price,
      currency: entry.currency,
      check_in: entry.check_in,
      check_out: entry.check_out,
      rooms: entry.rooms,
      guests: entry.guests,
    }).catch(() => { /* offline / 401 — local copy still fine */ });
  }
};

export const getRecentHotels = () => {
  const cutoff = Date.now() - HOTEL_TTL_MS;
  const fresh = read(KEYS.hotels).filter((h) => h?.ts && h.ts >= cutoff);
  return fresh.slice(0, MAX_VIEWED_HOTELS);
};

export const clearRecentHotels = () => {
  write(KEYS.hotels, []);
  if (isCustomerLoggedIn()) {
    api.delete('/customer/recent-hotels').catch(() => {});
  }
};

// Called once on login: push local guest history to the account, then pull
// the merged list back and replace local storage. Idempotent, safe to retry.
export const syncRecentHotelsWithAccount = async () => {
  if (!isCustomerLoggedIn()) return;
  const local = getRecentHotels();
  try {
    if (local.length > 0) {
      await api.post('/customer/recent-hotels/batch', {
        items: local.map((h) => ({
          hotel_id: h.id, name: h.name, image: h.image, city: h.city, area: h.area,
          provider: h.provider, star_rating: h.star_rating, review_rating: h.review_rating,
          google_rating: h.google_rating, price: h.price, currency: h.currency,
          check_in: h.check_in, check_out: h.check_out, rooms: h.rooms, guests: h.guests,
        })),
      });
    }
    const { data } = await api.get('/customer/recent-hotels');
    const merged = (data?.items || []).map((it) => ({
      id: it.hotel_id, name: it.name, image: it.image, city: it.city, area: it.area,
      provider: it.provider, star_rating: it.star_rating, review_rating: it.review_rating,
      google_rating: it.google_rating, price: it.price, currency: it.currency,
      check_in: it.check_in, check_out: it.check_out, rooms: it.rooms, guests: it.guests,
      ts: it.viewed_at ? new Date(it.viewed_at).getTime() : Date.now(),
    }));
    write(KEYS.hotels, merged);
  } catch { /* sync is best-effort */ }
};

// ─── Recently viewed flights ─────────────────────────────────────────────
export const recordFlightView = (flight) => {
  if (!flight?.id) return;
  const list = read(KEYS.flights);
  const next = dedupeAndLimit(list, { ...flight, ts: Date.now() }, MAX_VIEWED_FLIGHTS, (e) => e.id);
  write(KEYS.flights, next);
};
export const getRecentFlights = () => read(KEYS.flights);

// ─── React hook ──────────────────────────────────────────────────────────
export const useRecent = (kind) => {
  const reader = kind === 'searches' ? getRecentSearches
              : kind === 'hotels'   ? getRecentHotels
              : kind === 'flights'  ? getRecentFlights
              : () => [];
  const [items, setItems] = useState(reader);

  useEffect(() => {
    const sync = () => setItems(reader());
    window.addEventListener('recent-updated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('recent-updated', sync);
      window.removeEventListener('storage', sync);
    };
  }, [kind, reader]);

  return items;
};
